import { useState, useCallback } from 'react';
import config from '../../config';
import { buildFieldDataPayload } from '../../utils/formFieldBuckets';
import { buildTaxonomyPayload } from '../../utils/taxonomy';

export default function useWizardSubmit({ project, user, isExhibitionMode }) {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    ({ wizardState, filesByField, selectionType }) => {
      return new Promise((resolve, reject) => {
        setIsSubmitting(true);
        setError(null);

        const formData = new FormData();

        const uid =
          user && !isExhibitionMode
            ? user.uid
            : `${wizardState.contFirstName || ''}_${wizardState.contLastName || ''}_${wizardState.contEmail || ''}`;

        formData.append('uid', uid);
        formData.append('year', wizardState.year);
        formData.append('form_id', wizardState.formId);
        formData.append('projectsid', project?.projectsid || '');
        const shapeType = wizardState.shapeType || selectionType || 'marker';
        formData.append('shape_type', shapeType);

        let locId;
        if (shapeType === 'polygon' && Array.isArray(wizardState.polygonCoordinates) && wizardState.polygonCoordinates.length >= 3) {
          locId = JSON.stringify(wizardState.polygonCoordinates);
        } else if (shapeType === 'line' && Array.isArray(wizardState.lineCoordinates) && wizardState.lineCoordinates.length >= 2) {
          locId = JSON.stringify(wizardState.lineCoordinates);
        } else if (wizardState.latLng) {
          locId = `${parseFloat(wizardState.latLng.lat).toFixed(6)},${parseFloat(wizardState.latLng.lng).toFixed(6)}`;
        } else {
          locId = '';
        }
        formData.append('loc_id', locId);

        const fieldData = buildFieldDataFromWizardState(wizardState);
        formData.append('field_data', JSON.stringify(fieldData));
        formData.append('visibility', wizardState.visibility);

        // Slice 5 (Phase 3 #9): chip assignments. submitDynDataNU.php
        // parses both the bracketed `taxonomy_ids[]` POST shape and a
        // CSV/JSON `taxonomy_ids` scalar (parseTaxonomyIdsFromInput).
        //
        // PR #45 — v2 ranked themes. Client July 2026 (#7) extended the
        // ranked Primary/Secondary/Tertiary model to Property Type and
        // Communities, so we build the id list from all three ranked
        // slot maps (`themeRanks` / `propertyTypeRanks` / `communityRanks`).
        // The flat array path stays as a fallback for any legacy/test
        // caller that still mutates `taxonomyIds` directly; the single
        // `propertyTypeId`/`communityId` fields stay wired for the same
        // reason (buildTaxonomyPayload folds them in if present).
        //
        // We ship BOTH `taxonomy_ids[]` (canonical id list, all chips)
        // AND `taxonomy_ranks` (JSON rank overlay for every ranked kind).
        // The backend treats taxonomy_ids as the source of truth for
        // "what's assigned" and stores `rank` per (shape, taxonomy)
        // generically, so ranked PT/community persist with no backend
        // change. Old PHP that doesn't know about ranks silently ignores
        // `taxonomy_ranks` and gets DEFAULT rank=1.
        //
        // buildTaxonomyPayload is the shared serializer (used by the
        // ContributionComp create/edit forms too) so every entry point
        // emits a byte-identical payload.
        const { taxonomyIds, taxonomyRanks } = buildTaxonomyPayload({
          themeRanks: wizardState.themeRanks,
          propertyTypeRanks: wizardState.propertyTypeRanks,
          communityRanks: wizardState.communityRanks,
          propertyTypeId: wizardState.propertyTypeId,
          communityId: wizardState.communityId,
          taxonomyIds: wizardState.taxonomyIds,
        });

        taxonomyIds.forEach((id) => {
          formData.append('taxonomy_ids[]', String(id));
        });

        if (taxonomyRanks.length > 0) {
          formData.append('taxonomy_ranks', JSON.stringify(taxonomyRanks));
        }

        // Iterate the actual file buckets keyed by field_id, mirroring the
        // legacy ContributionComp `for (const fieldId in files)` loop. The
        // previous filter-through-formFields path silently dropped uploads
        // when wizardState.formFields was empty/stale at submit time (e.g.
        // FormHandler races, or future code that reshapes the form payload).
        // The UI step that adds files (StoryStep) already restricts pickers
        // to fields whose field_type === 'file', so any key here is by
        // construction a valid file field — no need to re-validate.
        if (filesByField) {
          Object.entries(filesByField).forEach(([fieldId, entries]) => {
            (entries || []).forEach((entry) => {
              if (!entry?.file) return;
              formData.append(`file_${fieldId}[]`, entry.file);
              formData.append(`caption_${fieldId}[]`, entry.caption || '');
            });
          });
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', config.getApiUrl('submitDynDataNU.php'));

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          setIsSubmitting(false);
          setProgress(0);
          try {
            const result = JSON.parse(xhr.responseText);
            if (result.success) {
              resolve(result);
            } else {
              const msg = result.message || 'Submission failed.';
              setError(msg);
              reject(new Error(msg));
            }
          } catch (e) {
            setError('Invalid server response.');
            reject(e);
          }
        };

        xhr.onerror = () => {
          setIsSubmitting(false);
          setProgress(0);
          setError('An error occurred during submission.');
          reject(new Error('Network error'));
        };

        xhr.send(formData);
      });
    },
    [project, user, isExhibitionMode],
  );

  return { submit, progress, error, isSubmitting, setError };
}

function buildFieldDataFromWizardState(wizardState) {
  const { formFields, address, placeName, caption, storyText, additionalFields } = wizardState;
  return buildFieldDataPayload(formFields, {
    address,
    placeName,
    caption,
    storyText,
    additionalValues: additionalFields || {},
  });
}

